import { DataStreamBaseTileConfig, getClientDataStreamRequest } from '@squaredup/data-streams';
import { useDashboardContext } from 'contexts/DashboardContext';
import { useDataStreamWorkspaceContext } from 'contexts/DataStreamWorkspaceContext';
import { useClientDataStreamsContext } from 'dashboard-engine/dataStreams/clientDataStreams/ClientDataStreamsContext';
import { getDataStreamDataFn, parseUseDataStreamArgs } from 'dashboard-engine/hooks/useDataStream';
import { getBaseRequestConfig, getDataStreamOptions } from 'dashboard-engine/hooks/useDataStreamConfigToRequest';
import { getDataStreamDefinition } from 'dashboard-engine/hooks/useDataStreamFromTileConfig';
import { useDataStreamDefinitionsForWorkspace } from 'queries/hooks/useDataStreamDefinitions';
import { useQueries } from 'react-query';
import { useDatasetContext } from '../../contexts/DatasetContext';

/**
 * Hook to get the column names for all datasets in the tile.
 * Dataset data stream requests will be retrieved from the cache if possible.
 * These column names are used in the SQL Editor intellisense.
 */
export const useDatasetColumns = () => {
    const { datasets } = useDatasetContext();
    const { data: streamDefinitions = [], isFetched: streamDefinitionLoaded } = useDataStreamDefinitionsForWorkspace();

    const dashboardContext = useDashboardContext();

    const clientDataStreams = useClientDataStreamsContext();

    const { isGlobal, workspace } = useDataStreamWorkspaceContext();

    const context = {};

    const validDatasets = datasets as {
        tableName: string;
        // No need for Partial here
        config: DataStreamBaseTileConfig;
    }[];

    const datasetResults = useQueries(
        validDatasets.map(({ config }) => {
            const { dataStreamDefinition, isConfigured } = getDataStreamDefinition(
                config,
                streamDefinitions,
                streamDefinitionLoaded
            );

            const dataStreamOptions = getDataStreamOptions(config, {}, 'dashboard');

            const requestConfig = getBaseRequestConfig(config, dataStreamDefinition);

            const dataStreamRequest =
                dataStreamDefinition?.id == null || !isConfigured
                    ? undefined
                    : getClientDataStreamRequest(
                          requestConfig,
                          config.timeframe ?? dashboardContext.timeframe,
                          dataStreamOptions,
                          context
                      );

            const args = parseUseDataStreamArgs(dataStreamRequest, {});

            return {
                queryKey: args.queryKey,
                queryFn: getDataStreamDataFn(args, isGlobal ? null : workspace, clientDataStreams),
                enabled: Boolean(dataStreamRequest)
            };
        })
    );

    return datasets.map((dataset, index) => {
        const result = datasetResults[index];
        return {
            tableName: dataset.tableName,
            columns:
                result.data?.metadata.columns.map((c) => ({
                    name: c.name,
                    displayName: c.displayName
                })) ?? [],
            isLoading: result.isLoading
        };
    });
};
